import { ListSubheader, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface SelectGroupHeaderProps {
  label: string;
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * Collapsible group header for grouped <Select> menus.
 *
 * Rendered as a ListSubheader, not a MenuItem, on purpose: MUI's Select treats
 * every focusable child as an option, so a MenuItem with no `value` pushes
 * `undefined` into a `multiple` Select's value (which reaches the API as
 * `null`) and clears/closes a single Select. A ListSubheader carries no
 * tabindex and is skipped by MenuList's highlight logic, so Select ignores
 * clicks on it; the toggle runs on mousedown, which Select never intercepts.
 */
export function SelectGroupHeader({ label, collapsed, onToggle }: SelectGroupHeaderProps) {
  return (
    <ListSubheader
      disableSticky
      onMouseDown={(e) => {
        e.preventDefault();
        onToggle();
      }}
      sx={{ bgcolor: 'action.hover', py: 0.75, lineHeight: 'normal', cursor: 'pointer', userSelect: 'none' }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
        {collapsed
          ? <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        <Typography variant="body2" fontWeight={700}>{label}</Typography>
      </Stack>
    </ListSubheader>
  );
}

// Keeps MenuList from making the header the initially highlighted item, which
// would give it a tabindex and make Select treat it as a selectable option.
SelectGroupHeader.muiSkipListHighlight = true;
